import React, { useMemo, useState } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { sanitizeHTML } from '../utils/security'
import { resolveTeamCategory } from '../utils/teamCategory'
import { CareerHistoryEntry, Player } from '../types'
import ImageUpload from './ImageUpload'
import { Layout, Users, Target, Activity, Mail, Youtube, FileText, Star, Save, Plus, Trash2, History, Lock } from 'lucide-react'
import {
  APPROVED_COMPETITION_EXAMPLES,
  COMPETITION_POLICY_NOTICE,
  CUSTOM_COMPETITION_VALUE,
  collectCompetitionNames,
  getCompetitionPolicyViolation,
  inferCompetitionId,
} from '../utils/competitions'

const POSITIONS = ['GK', 'CB', 'RB', 'LB', 'RWB', 'LWB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'SS', 'CF']
const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const STATES = [
  'Lagos', 'Ogun', 'Oyo', 'Osun', 'Ondo', 'Ekiti', 'Akure',
  'Abuja', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto',
  'Delta', 'Rivers', 'Bayelsa', 'Cross River', 'Cameroon', 'Akwa Ibom',
  'Enugu', 'Ebonyi', 'Anambra', 'Imo', 'Abia', 'Taraba', 'Adamawa',
  'Gombe', 'Borno', 'Yobe', 'Jigawa', 'Kogi', 'Kwara', 'Niger', 'Nasarawa', 'Plateau'
]
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Injured', label: 'Injured' },
  { value: 'Loaned Out', label: 'Loaned Out' },
  { value: 'On-Loan', label: 'On-Loan' },
  { value: 'Trial', label: 'Trial' },
]

const sanitizeCompetitionSelections = (competitions: string[]) =>
  Array.from(new Set(competitions.map((competition) => competition.trim()).filter(Boolean)))

interface LogPlayerProps {
  isClubMode?: boolean;
  onSuccess?: () => void;
  clubId?: string;
  clubName?: string;
}

type CompetitionPerformanceDraft = {
  id: string
  competition_id: string
  competition_name: string
  appearance_date: string
  opponent: string
  venue: string
  minutes: string
  goals: string
  assists: string
  shots: string
  key_passes: string
  dribbles: string
  tackles: string
  rating: string
  notes: string
}

function LogPlayer({ isClubMode, onSuccess, clubId, clubName }: LogPlayerProps) {
  const { addPlayer, clubs, competitions } = usePlayerStore()
  const { user } = useAuthStore()
  const isAdminRole = user?.role === 'admin'
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [photoUrl, setPhotoUrl] = useState('')
  const [careerHistory, setCareerHistory] = useState<CareerHistoryEntry[]>([])
  const [competitionPerformances, setCompetitionPerformances] = useState<CompetitionPerformanceDraft[]>([])
  const [customCompetitionName, setCustomCompetitionName] = useState('')

  // Determine default club value
  const defaultClub = clubName ?? (isClubMode ? (user?.org || user?.name || '') : '')
  const competitionOptions = useMemo(
    () => collectCompetitionNames({ competitions, clubs }),
    [clubs, competitions]
  )

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    positions: [] as string[],
    main_position: '',
    club: defaultClub,
    league: '',
    competitions: [] as string[],
    state: '',
    lga: '',
    foot: 'Right',
    height: '',
    weight: '',
    goals: '',
    assists: '',
    apps: '0',
    shots: '',
    keyPasses: '',
    dribbles: '',
    duels: '',
    minutesPlayed: '',
    potential: '1',
    youtube: '',
    technical: '5',
    physical: '5',
    mental: '5',
    notes: '',
    match_desc: '',
    jersey_number: '',
    team_category: 'First Team',
    status: 'Active'
  })
  const savedCompetitionNames = sanitizeCompetitionSelections(formData.competitions || [])
  const competitionSelections = sanitizeCompetitionSelections([
    ...savedCompetitionNames,
    customCompetitionName,
  ])
  const primaryCompetitionName = competitionSelections[0] || ''

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePhotoUpload = (url: string) => {
    setPhotoUrl(url)
  }

  const updateCareerEntry = (id: string, field: keyof CareerHistoryEntry, value: string) => {
    setCareerHistory((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value as any } : entry))
    )
  }

  const addCareerEntry = () => {
    setCareerHistory((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        club: '',
        years: '',
        goals: undefined,
        assists: undefined,
        transaction_fee: '',
      },
    ])
  }

  const removeCareerEntry = (id: string) => {
    setCareerHistory((prev) => prev.filter((entry) => entry.id !== id))
  }

  const addCompetitionPerformance = () => {
    const initialCompetition = primaryCompetitionName || competitionOptions[0] || ''
    const competitionId = inferCompetitionId(initialCompetition, competitions) || ''
    setCompetitionPerformances((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        competition_id: competitionId,
        competition_name: initialCompetition,
        appearance_date: '',
        opponent: '',
        venue: '',
        minutes: '0',
        goals: '0',
        assists: '0',
        shots: '',
        key_passes: '',
        dribbles: '',
        tackles: '',
        rating: '',
        notes: '',
      },
    ])
  }

  const updateCompetitionPerformance = (id: string, field: keyof CompetitionPerformanceDraft, value: string) => {
    setCompetitionPerformances((prev) =>
      prev.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              [field]:
                field === 'competition_id'
                  ? value
                  : field === 'competition_name'
                    ? value
                    : value,
            }
          : entry
      )
    )
  }

  const removeCompetitionPerformance = (id: string) => {
    setCompetitionPerformances((prev) => prev.filter((entry) => entry.id !== id))
  }

  const toggleCompetitionSelection = (competitionName: string) => {
    setFormData((prev) => {
      const nextSelections = prev.competitions?.includes(competitionName)
        ? prev.competitions.filter((competition) => competition !== competitionName)
        : [...(prev.competitions || []), competitionName]

      return {
        ...prev,
        competitions: sanitizeCompetitionSelections(nextSelections),
        league: sanitizeCompetitionSelections(nextSelections)[0] || '',
      }
    })
  }

  const addCustomCompetitionToProfile = () => {
    const competitionName = customCompetitionName.trim()
    if (!competitionName) return

    setFormData((prev) => {
      const nextSelections = sanitizeCompetitionSelections([...(prev.competitions || []), competitionName])
      return {
        ...prev,
        competitions: nextSelections,
        league: nextSelections[0] || '',
      }
    })
    setCustomCompetitionName('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!formData.name.trim()) {
      setError('Player name is required')
      return
    }
    if (!formData.age || isNaN(parseInt(formData.age))) {
      setError('Valid age is required')
      return
    }
    if (formData.positions.length === 0) {
      setError('At least one position is required')
      return
    }
    if (!formData.main_position) {
      setError('Main position is required')
      return
    }
    const competitionSelections = sanitizeCompetitionSelections([
      ...(formData.competitions || []),
      customCompetitionName,
    ])
    const competitionViolation = competitionSelections
      .map((competition) => getCompetitionPolicyViolation(competition))
      .find(Boolean)
    if (competitionViolation) {
      setError(competitionViolation)
      return
    }
    const invalidPerformance = competitionPerformances.find((entry) =>
      getCompetitionPolicyViolation(entry.competition_name)
    )
    if (invalidPerformance) {
      setError(getCompetitionPolicyViolation(invalidPerformance.competition_name))
      return
    }

    setLoading(true)
    try {
      // Determine club ID for ownership
      let ownerClubId: string | undefined = undefined
      if (isClubMode) {
        if (clubId) {
          ownerClubId = clubId
        } else {
          // fallback to user's own club
          const userClub = user?.id ? clubs.find(club => club.id === user.id || (club as any).scout_id === user.id || club.scoutId === user.id) : null
          ownerClubId = userClub?.id
        }
      }

      const player: any = {
        name: sanitizeHTML(formData.name),
        age: parseInt(formData.age),
        positions: formData.positions,
        main_position: formData.main_position,
        club: sanitizeHTML(formData.club),
        league: sanitizeHTML(competitionSelections[0] || ''),
        competitions: competitionSelections.map((competition) => sanitizeHTML(competition)),
        state: formData.state,
        lga: sanitizeHTML(formData.lga),
        foot: formData.foot,
        height: formData.height ? parseFloat(formData.height) : undefined,
        weight: formData.weight ? parseInt(formData.weight) : undefined,
        goals: formData.goals ? parseInt(formData.goals) : 0,
        assists: formData.assists ? parseInt(formData.assists) : 0,
        apps: formData.apps ? parseInt(formData.apps) : 0,
        shots: formData.shots ? parseInt(formData.shots) : 0,
        kp: formData.keyPasses ? parseInt(formData.keyPasses) : 0,
        dr: formData.dribbles ? parseInt(formData.dribbles) : 0,
        du: formData.duels ? parseInt(formData.duels) : 0,
        mins: formData.minutesPlayed ? parseInt(formData.minutesPlayed) : 0,
        potential: parseInt(formData.potential),
        technical: parseInt(formData.technical),
        physical: parseInt(formData.physical),
        mental: parseInt(formData.mental),
        notes: sanitizeHTML(formData.notes),
        match_desc: sanitizeHTML(formData.match_desc),
        career_history: careerHistory
          .filter((entry) => entry.club.trim() || entry.years.trim() || entry.goals !== undefined || entry.assists !== undefined || entry.transaction_fee?.trim())
          .map((entry) => ({
            ...entry,
            club: sanitizeHTML(entry.club),
            years: sanitizeHTML(entry.years),
            transaction_fee: entry.transaction_fee?.trim() ? sanitizeHTML(entry.transaction_fee.trim()) : undefined,
          })),
        scout: user?.email || 'Unknown',
        youtube: formData.youtube,
        photo_url: photoUrl,
        is_verified: false,
        owner_club_id: ownerClubId,
        jersey_number: isClubMode && formData.jersey_number ? parseInt(formData.jersey_number) : undefined,
        team_category: isClubMode ? resolveTeamCategory(formData.team_category, parseInt(formData.age)) : undefined,
        status: isClubMode ? (formData.status as Player['status']) : undefined,
        is_public: true,
        competition_performances: competitionPerformances
          .filter((entry) => entry.competition_name.trim())
          .map((entry) => ({
            ...entry,
            competition_id: entry.competition_id || inferCompetitionId(entry.competition_name, competitions) || undefined,
            competition_name: entry.competition_name.trim(),
            appearance_date: entry.appearance_date || undefined,
            opponent: entry.opponent || undefined,
            venue: entry.venue || undefined,
            minutes: entry.minutes ? parseInt(entry.minutes) : 0,
            goals: entry.goals ? parseInt(entry.goals) : 0,
            assists: entry.assists ? parseInt(entry.assists) : 0,
            shots: entry.shots ? parseInt(entry.shots) : undefined,
            key_passes: entry.key_passes ? parseInt(entry.key_passes) : undefined,
            dribbles: entry.dribbles ? parseInt(entry.dribbles) : undefined,
            tackles: entry.tackles ? parseInt(entry.tackles) : undefined,
            rating: entry.rating ? parseFloat(entry.rating) : undefined,
            notes: sanitizeHTML(entry.notes || ''),
          })),
      }

      await addPlayer(player)
      setSuccess(`✓ ${formData.name} logged successfully!`)

      // Reset form but keep default club
      setFormData(prev => ({
        ...prev,
        name: '',
        age: '',
        positions: [],
        main_position: '',
        club: defaultClub,
        league: '',
        competitions: [],
        state: '',
        lga: '',
        foot: 'Right',
        height: '',
        weight: '',
        goals: '',
        assists: '',
        apps: '0',
        shots: '',
        keyPasses: '',
        dribbles: '',
        duels: '',
        minutesPlayed: '',
        potential: '1',
        youtube: '',
        technical: '5',
        physical: '5',
        mental: '5',
        notes: '',
        match_desc: '',
        jersey_number: '',
        team_category: 'First Team',
        status: 'Active'
      }))
      setPhotoUrl('')
      setCompetitionPerformances([])
      setCustomCompetitionName('')

      if (onSuccess) {
        setTimeout(onSuccess, 1500)
      } else {
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log player')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <div className="bg-[#021a1a] border border-white/5 shadow-2xl rounded-[32px] overflow-hidden">
        {/* Header */}
        <div className="p-8 border-b border-white/5 bg-white/2">
          <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">
            {isClubMode ? 'Enroll Player to Squad' : 'Scout New Talent'}
          </h2>
          <p className="text-gray-500 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Official Database Registration Module</p>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-900/10 border border-red-900/20 rounded-2xl text-red-400 text-xs font-bold uppercase tracking-widest text-center">
              ALERT: {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-[#7cff01]/10 border border-[#7cff01]/20 rounded-2xl text-[#7cff01] text-xs font-black uppercase tracking-widest text-center">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-12">
            {/* 1. Identity Module */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                <Users size={16} /> Identity & Bio
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="E.g. Jay-Jay Okocha"
                    className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Current Age *</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    placeholder="18"
                    className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                  />
                </div>
              </div>
            </div>

            {/* 2. Tactical Module */}
            <div className="space-y-6">
              <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                <Target size={16} /> Tactical Role
              </h3>
              <div className="space-y-4">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Select Capable Positions *</label>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {POSITIONS.map(pos => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => {
                        const newPositions = formData.positions.includes(pos)
                          ? formData.positions.filter(p => p !== pos)
                          : [...formData.positions, pos]
                        setFormData(prev => ({ 
                          ...prev, 
                          positions: newPositions,
                          main_position: newPositions.includes(prev.main_position) ? prev.main_position : (newPositions[0] || '')
                        }));
                       }}
                      className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition border ${
                        formData.positions.includes(pos)
                          ? 'bg-[#7cff01] border-[#7cff01] text-black shadow-lg shadow-[#7cff01]/10'
                          : 'bg-white/5 border-white/5 text-gray-500 hover:border-white/20'
                      }`}
                    >
                      {pos}
                    </button>
                  ))}
                </div>
                
                {formData.positions.length > 0 && (
                  <div className="animate-in fade-in duration-300">
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Primary Specialization *</label>
                    <select
                      name="main_position"
                      value={formData.main_position}
                      onChange={handleChange}
                      className="w-full px-5 py-3 bg-[#7cff01]/10 border border-[#7cff01]/20 rounded-2xl text-[#7cff01] font-black uppercase tracking-widest outline-none transition"
                    >
                      {formData.positions.map(pos => (
                        <option key={pos} value={pos} className="bg-gray-900">{pos}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* 3. Physical & Squad Module */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Activity size={16} /> Physicality
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Height (CM)</label>
                    <input type="number" name="height" value={formData.height} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-[#7cff01] transition font-bold" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Weight (KG)</label>
                    <input type="number" name="weight" value={formData.weight} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-[#7cff01] transition font-bold" />
                  </div>
                </div>
                <select name="foot" value={formData.foot} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition">
                  <option value="Right" className="bg-gray-900">Right Footed</option>
                  <option value="Left" className="bg-gray-900">Left Footed</option>
                  <option value="Both" className="bg-gray-900">Ambidextrous</option>
                </select>
              </div>

              {isClubMode && (
                <div className="space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                    <Layout size={16} /> Squad Management
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Jersey #</label>
                      <input type="number" name="jersey_number" value={formData.jersey_number} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-[#7cff01] transition font-bold" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Status</label>
                      <select name="status" value={formData.status} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white font-bold outline-none">
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value} className="bg-gray-900">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 4. Competition Context */}
            <div className="space-y-6 p-8 bg-white/2 rounded-[32px] border border-white/5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Activity size={16} /> Competition Context
                </h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                  {savedCompetitionNames.length > 0 ? `${savedCompetitionNames.length} selected` : 'Optional'}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Select one or more competitions the player participates in
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                    {competitionOptions.map((competition) => {
                      const checked = formData.competitions.includes(competition)
                      return (
                        <label
                          key={competition}
                          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 transition cursor-pointer ${
                            checked
                              ? 'bg-[#7cff01]/10 border-[#7cff01]/30'
                              : 'bg-white/5 border-white/5 hover:border-white/15'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCompetitionSelection(competition)}
                            className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent text-[#7cff01] focus:ring-[#7cff01]"
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-white truncate">{competition}</span>
                            <span className="block text-[11px] text-gray-500 mt-1">
                              {inferCompetitionId(competition, competitions) ? 'Competition directory entry' : 'Imported or custom competition'}
                            </span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[24px] border border-white/5 bg-white/2 p-5">
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">Saved competitions</p>
                    {savedCompetitionNames.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {savedCompetitionNames.map((competition) => (
                          <span
                            key={competition}
                            className="inline-flex items-center rounded-full border border-[#7cff01]/20 bg-[#7cff01]/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-[#7cff01]"
                          >
                            {competition}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No competition selected yet.</p>
                    )}
                    <p className="text-sm text-gray-500 mt-3">
                      The first selected competition is kept as the profile&apos;s primary label for legacy match recording.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">
                      Add a custom competition
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={customCompetitionName}
                        onChange={(e) => setCustomCompetitionName(e.target.value)}
                        placeholder="Type a competition name"
                        className="flex-1 px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                      />
                      <button
                        type="button"
                        onClick={addCustomCompetitionToProfile}
                        className="px-5 py-3 rounded-2xl bg-[#7cff01] text-black text-xs font-black uppercase tracking-widest hover:opacity-90 transition"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Evaluation Module */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-6">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <FileText size={16} /> Scouting Report
                </h3>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Provide a detailed scout summary..."
                  rows={6}
                  className="w-full px-5 py-4 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-medium"
                />
              </div>
              {isAdminRole ? (
                <div className="space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                    <Star size={16} /> Assessment
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Technical', name: 'technical' },
                      { label: 'Physical', name: 'physical' },
                      { label: 'Mental', name: 'mental' }
                    ].map(skill => (
                      <div key={skill.name}>
                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1">{skill.label}</label>
                        <select name={skill.name} value={(formData as any)[skill.name]} onChange={handleChange} className="w-full px-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white font-bold outline-none">
                          {RATINGS.map(r => <option key={r} value={r} className="bg-gray-900">{r}/10</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                    <Star size={16} /> Assessment
                  </h3>
                  <div className="border border-dashed border-gray-800 rounded-2xl p-5 bg-white/5 flex flex-col items-start gap-3">
                    <Lock size={18} className="text-gray-500" />
                    <p className="text-[11px] text-gray-400 leading-relaxed uppercase tracking-wider font-bold">
                      Talent assessment (technical, physical, mental & potential) is set exclusively by Scouta admins.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 6. Media & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Youtube size={16} /> Digital Assets
                </h3>
                <div className="space-y-4">
                  <input type="url" name="youtube" value={formData.youtube} onChange={handleChange} placeholder="YouTube Highlights URL" className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-[#7cff01]" />
                  <div className="p-4 bg-white/2 rounded-2xl border border-white/5">
                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-4">Official Photo</label>
                    <ImageUpload onUpload={handlePhotoUpload} currentUrl={photoUrl} />
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Mail size={16} /> Club & Location
                </h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    name="club" 
                    value={formData.club} 
                    onChange={handleChange} 
                    placeholder="Current Club" 
                    list="club-list"
                    className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none focus:border-[#7cff01]" 
                  />
                  <datalist id="club-list">
                    {clubs.map(club => (
                      <option key={club.id} value={club.name}>{club.name}</option>
                    ))}
                  </datalist>
                  <div className="grid grid-cols-2 gap-4">
                    <select name="state" value={formData.state} onChange={handleChange} className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none">
                      <option value="" className="bg-gray-900">Select State</option>
                      {STATES.map(s => <option key={s} value={s} className="bg-gray-900">{s}</option>)}
                    </select>
                    <input type="text" name="lga" value={formData.lga} onChange={handleChange} placeholder="LGA" className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white outline-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Historical Data */}
            <div className="space-y-6">
               <div className="flex items-center justify-between gap-4">
                  <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                     <History size={16} /> Historical Data
                  </h3>
                <button
                  type="button"
                  onClick={addCareerEntry}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/5 text-white text-xs font-black uppercase tracking-widest hover:bg-white/10 transition"
                >
                  <Plus size={14} />
                  Add Entry
                </button>
              </div>

              {careerHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/2 p-6 text-center">
                  <p className="text-xs font-black uppercase tracking-widest text-gray-500">No historical data added yet</p>
                  <p className="text-[11px] text-gray-600 mt-2">Add previous clubs, year ranges, goals, assists, and transaction fee.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {careerHistory.map((entry) => (
                    <div key={entry.id} className="rounded-[24px] border border-white/5 bg-white/2 p-5 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Career Record</p>
                        <button
                          type="button"
                          onClick={() => removeCareerEntry(entry.id)}
                          className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-widest hover:text-red-300 transition"
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Club</label>
                          <input
                            type="text"
                            value={entry.club}
                            onChange={(e) => updateCareerEntry(entry.id, 'club', e.target.value)}
                            placeholder="Club name"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Years</label>
                          <input
                            type="text"
                            value={entry.years}
                            onChange={(e) => updateCareerEntry(entry.id, 'years', e.target.value)}
                            placeholder="2020-2023"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Goals</label>
                          <input
                            type="number"
                            value={entry.goals ?? ''}
                            onChange={(e) => updateCareerEntry(entry.id, 'goals', e.target.value)}
                            placeholder="0"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Assists</label>
                          <input
                            type="number"
                            value={entry.assists ?? ''}
                            onChange={(e) => updateCareerEntry(entry.id, 'assists', e.target.value)}
                            placeholder="0"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:border-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Transaction Fee</label>
                          <input
                            type="text"
                            value={entry.transaction_fee || ''}
                            onChange={(e) => updateCareerEntry(entry.id, 'transaction_fee', e.target.value)}
                            placeholder="Optional, e.g. 1000000 or leave blank"
                            className="w-full px-5 py-3 bg-white/5 border border-white/5 rounded-2xl text-white placeholder-gray-600 focus:ring-2 focus:border-[#7cff01]/20 focus:border-[#7cff01] outline-none transition font-bold"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                 </div>
               )}
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-xs font-black text-[#7cff01] uppercase tracking-[0.2em] flex items-center gap-3">
                  <Activity size={16} /> Competition Performance
                </h3>
                <button
                  type="button"
                  onClick={addCompetitionPerformance}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/5 text-white text-xs font-black uppercase tracking-widest hover:bg-white/10 transition"
                >
                  <Plus size={14} />
                  Add Record
                </button>
              </div>

              {competitionPerformances.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/2 p-6 text-center">
                  <p className="text-xs font-black uppercase tracking-widest text-gray-500">No competition record added yet</p>
                  <p className="text-[11px] text-gray-600 mt-2">Record player output for a recognized competition or tournament.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {competitionPerformances.map((entry) => (
                    <div key={entry.id} className="rounded-[24px] border border-white/5 bg-white/2 p-5 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Competition Entry</p>
                        <button
                          type="button"
                          onClick={() => removeCompetitionPerformance(entry.id)}
                          className="inline-flex items-center gap-2 text-red-400 text-[10px] font-black uppercase tracking-widest hover:text-red-300 transition"
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Competition</label>
                          <select
                            value={competitionOptions.includes(entry.competition_name) ? entry.competition_name : CUSTOM_COMPETITION_VALUE}
                            onChange={(e) => {
                              const value = e.target.value
                              if (value === CUSTOM_COMPETITION_VALUE) {
                                updateCompetitionPerformance(entry.id, 'competition_name', '')
                                updateCompetitionPerformance(entry.id, 'competition_id', '')
                                return
                              }
                              updateCompetitionPerformance(entry.id, 'competition_name', value)
                              updateCompetitionPerformance(entry.id, 'competition_id', inferCompetitionId(value, competitions) || '')
                            }}
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white font-bold outline-none focus:border-[#7cff01] transition"
                          >
                            <option value="" className="bg-gray-900">Select competition</option>
                            {competitionOptions.map((competition) => (
                              <option key={competition} value={competition} className="bg-gray-900">
                                {competition}
                              </option>
                            ))}
                            <option value={CUSTOM_COMPETITION_VALUE} className="bg-gray-900">Other recognized competition</option>
                          </select>
                          {(!competitionOptions.includes(entry.competition_name) || !entry.competition_name) && (
                            <input
                              type="text"
                              value={entry.competition_name}
                              onChange={(e) => {
                                updateCompetitionPerformance(entry.id, 'competition_name', e.target.value)
                                updateCompetitionPerformance(entry.id, 'competition_id', inferCompetitionId(e.target.value, competitions) || '')
                              }}
                              placeholder="Type a recognized competition or tournament"
                              className="mt-3 w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                            />
                          )}
                          <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                            {COMPETITION_POLICY_NOTICE}
                            {' '}
                            Examples: {APPROVED_COMPETITION_EXAMPLES.join(', ')}.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Date</label>
                            <input
                              type="date"
                              value={entry.appearance_date}
                              onChange={(e) => updateCompetitionPerformance(entry.id, 'appearance_date', e.target.value)}
                              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Rating</label>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={entry.rating}
                              onChange={(e) => updateCompetitionPerformance(entry.id, 'rating', e.target.value)}
                              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Opponent</label>
                          <input
                            type="text"
                            value={entry.opponent}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'opponent', e.target.value)}
                            placeholder="Opponent"
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Venue</label>
                          <input
                            type="text"
                            value={entry.venue}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'venue', e.target.value)}
                            placeholder="Stadium / venue"
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                          />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:col-span-2">
                          {[
                            { label: 'Minutes', key: 'minutes' },
                            { label: 'Goals', key: 'goals' },
                            { label: 'Assists', key: 'assists' },
                            { label: 'Shots', key: 'shots' },
                            { label: 'Key Passes', key: 'key_passes' },
                            { label: 'Dribbles', key: 'dribbles' },
                            { label: 'Tackles', key: 'tackles' },
                          ].map((stat) => (
                            <div key={stat.key}>
                              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">{stat.label}</label>
                              <input
                                type="number"
                                value={(entry as any)[stat.key]}
                                onChange={(e) => updateCompetitionPerformance(entry.id, stat.key as keyof CompetitionPerformanceDraft, e.target.value)}
                                className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white outline-none focus:border-[#7cff01] transition"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Performance Notes</label>
                          <textarea
                            value={entry.notes}
                            onChange={(e) => updateCompetitionPerformance(entry.id, 'notes', e.target.value)}
                            rows={3}
                            className="w-full px-5 py-3 bg-[#0d1117] border border-gray-800 rounded-2xl text-white placeholder-gray-500 outline-none focus:border-[#7cff01] transition"
                            placeholder="Short competition notes..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-8 border-t border-white/5">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-[#7cff01] hover:bg-[#8eff1a] disabled:opacity-50 text-black rounded-[24px] font-black uppercase tracking-[0.2em] shadow-xl shadow-[#7cff01]/20 transition-all transform hover:-translate-y-1 active:translate-y-0 flex items-center justify-center gap-3"
              >
                {loading ? <Activity className="animate-spin" size={20} /> : <Save size={20} />}
                {loading ? 'Processing Database Entry...' : 'Finalize Player Registration'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default LogPlayer
